import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SelectIdentitiesComponent, ModalActionsComponent } from '@app/components';

import { useTypedTranslation } from '@app/hooks';
import { getModalSwitch, getLoaderBtn, getAllOrganizationsShortDetails, setOrgsCount, loadingData, handleRemoveUsersFromRole } from '@app/store/actions';
import { organizationsService, rolesService } from '@app/services';
import { IActiveFilter, IUserDialogData, Order, FilterValuesEnum } from '@app/models';
import { AxiosResponse } from 'axios';
import { dialogComponentsListData } from '@app/helpers';

interface IProps {
    disableEdit: boolean;
}

export const RemoveUsersFromRoleDialog: React.SFC<IProps> = ({ disableEdit }) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const roleId = useSelector((state: any) => state.roles.role.Id);

    const [filteredUsers, setFilteredUsers] = useState<IUserDialogData[]>([]);
    const [localUsers, setLocalUsers] = useState<IUserDialogData[]>([]);
    const [selectAllLocal, setSelectAllLocal] = useState(false);
    const [usersList, setUsersList] = useState<any[]>([]);
    const [usersListCount, setUsersListCount] = useState<number>();
    const [orgs, setOrgs] = useState();
    const [isChecked, setIsChecked] = useState(true);
    const [hasMoreItems, setHasMoreItems] = useState();
    const [loadMore, setLoadMore] = useState<number>(dialogComponentsListData);
    const [dataLoading, isDataLoading] = useState<boolean>(false);

    useEffect(() => {
        handleGetListOfUsers(roleId, 'DisplayName', Order.asc, dialogComponentsListData, 0, true);
        handleGetOrganizationsShortDetailsData('name', Order.asc, dialogComponentsListData, 0, true);
    }, []);

    useEffect(() => {
        if (usersList) {
            const checkedRows = filteredUsers.filter((el: any) => {
                return el.checked === true;
            });

            let newRows = usersList.map((user: any) => {
                const data: IUserDialogData = {
                    checked: false,
                    odataType: user['@odata.type'],
                    created: user.Created,
                    createdBy: user.CreatedBy,
                    name: user.DisplayName,
                    emailAddress: user.EmailAddress,
                    hasSecondFactors: user.HasSecondFactors,
                    id: user.Id,
                    impersonationWindow: user.ImpersonationWindow,
                    isAccountOfRecord: user.IsAccountOfRecord,
                    isLockedOut: user.IsLockedOut,
                    isPasswordExpired: user.IsPasswordExpired,
                    lastModified: user.LastModified,
                    lastModifiedBy: user.LastModifiedBy,
                    lastSignedIn: user.LastSignedIn,
                    lockedOutUntil: user.LockedOutUntil,
                    organizationId: user.OrganizationId,
                    password: user.Password,
                    passwordLastChangedNoReset: user.PasswordLastChangedNoReset,
                    passwordLastChangedResetAfterExpired: user.PasswordLastChangedResetAfterExpired,
                    passwordSync: user.PasswordSync,
                    passwordSyncLastChanged: user.PasswordSyncLastChanged,
                    principalName: user.PrincipalName,
                    propertiesPresentOnDelete: user.PropertiesPresentOnDelete,
                    provisioningState: user.ProvisioningState,
                    supportsImpersonation: user.SupportsImpersonation,
                    syncSource: user.SyncSource,
                    tenantId: user.TenantId
                };

                checkedRows.forEach((user: IUserDialogData) => {
                    if (data.id === user.id) {
                        data.checked = true;
                    }
                });

                return data;
            });

            if (usersList.length === 0) {
                setFilteredUsers([]);
                setSelectAllLocal(false);
            } else {
                setLocalUsers(newRows);
                setFilteredUsers(newRows);
            }
        }
    }, [usersList]);

    const handleGetListOfUsers = (
        roleId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ) => {
        isDataLoading(true);
        rolesService.getUsersInRole(roleId, orderBy, asc, top, skip, isReset, filters)
            .then((response: AxiosResponse) => {
                setUsersListCount(response.data['@odata.count']);
                if (filters === undefined) {
                    setUsersList(usersList.concat(response.data.value));
                } else if (filters.length) {
                    setUsersList(response.data.value);
                }
            })
            .then(() => isDataLoading(false))
            .catch(() => {
                isDataLoading(false);
            });
    };

    const handleGetOrganizationsShortDetailsData = (
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ) => {
        if (isReset) {
            dispatch(getAllOrganizationsShortDetails([]));
        }
        organizationsService.getOrganizationsShortDetails(orderBy, asc, top, skip, isReset, filters)
            .then((response: AxiosResponse) => {
                if (isReset) {
                    dispatch(setOrgsCount(response.data['@odata.count']));
                }
                setOrgs(response.data.value);
                dispatch(loadingData(false));
            })
            .catch(() => {
                dispatch(loadingData(false));
            });
    };

    const handleSetHasMore = (value: any) => {
        setHasMoreItems(value);
    };

    const handleSetValueFilter = (value: any) => {
        const filters: IActiveFilter[] = [
            {
                name: 'contains',
                value: value,
                label: FilterValuesEnum.displayName,
                filterBy: `${FilterValuesEnum.displayName},${FilterValuesEnum.principalName}`
            }
        ];

        handleGetListOfUsers(roleId, FilterValuesEnum.displayName, Order.asc, loadMore, 0, true, filters);
    };

    const handleSetFiltered = (value: any) => {
        setFilteredUsers(value);
    };

    const handleCheck = (value: boolean) => {
        setIsChecked(value);
    };

    const handleRemoveUsers = () => {
        const checboxSelected = filteredUsers.filter((el: any) => {
            return el.checked === true;
        });
        dispatch(getLoaderBtn(true));
        dispatch(handleRemoveUsersFromRole(roleId, checboxSelected));
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleFetchMoreGroups = () => {
        if (hasMoreItems) {
            handleGetListOfUsers(roleId, 'DisplayName', Order.asc, dialogComponentsListData, loadMore, true);
            setLoadMore(loadMore + dialogComponentsListData);
        }
    };

    return (
        <>
            <SelectIdentitiesComponent
                filteredItems={filteredUsers}
                localItems={localUsers}
                nonMemberedItems={usersList}
                selectAll={selectAllLocal}
                orgs={orgs}
                setFilteredItems={handleSetFiltered}
                setHandleCheck={handleCheck}
                noMoreResults={translation('NO_USERS_TO_REMOVE_FROM_ROLE')}
                fetchMore={handleFetchMoreGroups}
                totalCount={usersListCount}
                setHasMoreItems={handleSetHasMore}
                hasMoreItems={hasMoreItems}
                setValueFilter={handleSetValueFilter}
                additionalDisplayText={'email-organization'}
                dataLoading={dataLoading}
                disableEdit={disableEdit}
                isDisableToRemoveAoR={true}
            />
            <ModalActionsComponent
                primaryBtn={disableEdit ? '' : translation('REMOVE_SELECTED')}
                secondaryBtn={disableEdit ? translation('CLOSE_BUTTON') : translation('CANCEL')}
                onClickPrimary={handleRemoveUsers}
                onClickSecondary={handleCancel}
                disabled={isChecked}
                id='removeUserFromRole'
                isDeleteAction={true}
                withBorder={true}
            />
        </>
    );
};